// Common
import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Nav } from "react-bootstrap";
import { Button, Card, Form, Col, Row } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import {
  faTrashCan,
  faClockRotateLeft,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import SlideOutMenuSectioned from "@components/SlideOutMenu/SlideOutMenuSectioned";
import { useDeleteClientMutation, useUpdateClientMutation } from "@generated";
import { getDate } from "@utils/helperFunctions";
// Client Specific
import useListClients from "./api";
import { Client } from "./types";

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [activeFilter, setActiveFilter] = useState<boolean>(true);
  const [clientToSearch, setClientToSearch] = useState("");
  const [filter, setFilter] = useState<{ name?: string; active?: boolean }>({
    active: true,
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateClient, {}] = useUpdateClientMutation();
  const [deleteClient, {}] = useDeleteClientMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>("active");
  const [clientsCount, setClientsCount] = useState({
    active: null,
    archived: null,
  });

  const { clients, pageMeta, isLoading, refetch } = useListClients({
    page: {
      number: pageNumber,
      size: pageSize,
    },
    filter,
  });

  const handleDelete = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    selectedIds.forEach((clientId) => {
      if (currentTab == "active") {
        deleteClient({ slug: accountSlug, id: parseInt(clientId) });
      } else if (currentTab == "archived") {
        let selectedClient = clients.find((c) => c.id == parseInt(clientId));

        let data = {
          id: parseInt(clientId),
          type: "clients",
          attributes: {
            name: selectedClient?.name || "",
            active: true,
          },
        };
        updateClient({
          slug: accountSlug,
          id: parseInt(clientId),
          body: { data },
        });
      }
    });
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const searchClientName = (clientName: string, activeFilter: boolean) => {
    setFilter({ name: clientName, active: activeFilter });
  };

  const handleClientSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchClientName(clientToSearch, activeFilter);
  };

  const columns: ColumnDef<Client>[] = [
    {
      header: "Client Name",
      cell(props) {
        return (
          <div
            className="clientName"
            onClick={() => {
              window.location.assign(
                `/admin/clients/${props.row.original.id}/edit`
              );
              // navigate(`${props.row.original.id}/edit`);
            }}
          >
            {props.row.original.name}
          </div>
        );
      },
    },
    {
      header: "MSA Date",
      cell(props) {
        return <div>{getDate(props.row.original.msaDate || "")}</div>;
      },
    },
    {
      header: "Domain",
      cell(props) {
        return <div>{props.row.original.domain}</div>;
      },
    },
  ];

  interface Column {
    header: string;
    cell: (props: any) => JSX.Element;
  }

  if (isLoading) {
    return <ScopeStackSpinner />;
  } else {
    return (
      <div className="clientsTable">
        <Card
          style={{
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid var(--gray-3-stroke, #D7D7D7)",
            background: " var(--white, #FFF)",
            boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Card.Header
            style={{
              backgroundColor: "#fff",
              lineHeight: "unset",
              display: "block",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="accordianTitle" style={{ margin: "0" }}>
                Clients
              </div>
              <Button
                className="squareGreenButton"
                onClick={() => window.location.assign("/admin/clients/new")}
              >
                Add A Client
              </Button>
            </div>
            <hr></hr>
            <div
              style={{
                display: "flex",
              }}
            >
              {selectedIds.length > 0 && (
                <Button
                  style={{
                    marginRight: "10px",
                    alignSelf: "flex-end",
                    background: "unset",
                    color: "var(--teal-1, #418172)",
                    fontWeight: "700",
                    width: "11em",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                  }}
                  onClick={handleDelete}
                >
                  {selectedIds.length} Selected{" "}
                  <span
                    style={{
                      borderLeft: "1px solid #D7D7D7",
                      height: "24px",
                      margin: "0 8px",
                    }}
                  ></span>
                  {currentTab == "active" && (
                    <FontAwesomeIcon
                      style={{ color: "black" }}
                      icon={faTrashCan}
                    />
                  )}
                  {currentTab == "archived" && (
                    <FontAwesomeIcon
                      style={{ color: "black" }}
                      icon={faClockRotateLeft}
                    />
                  )}
                </Button>
              )}

              <div style={{ width: "-webkit-fill-available" }}>
                <FormFieldLabel label="Search Clients" className={undefined} />
                <Form
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      console.log("I should be searching");
                      handleClientSearch(e);
                    }
                  }}
                  onSubmit={handleClientSearch}
                >
                  <SearchField
                    placeholder="Search by client name"
                    onChange={(e) => {
                      setPageNumber(1);
                      setClientToSearch(e.target.value);
                    }}
                    value={clientToSearch}
                    onClick={() => {
                      setClientToSearch("");
                      searchClientName("", activeFilter);
                    }}
                    fullWidth
                  />
                </Form>
              </div>
            </div>
          </Card.Header>
          <Nav
            variant="underline"
            defaultActiveKey="active"
            onSelect={(k) => {
              setCurrentTab(k || "active");
              setSelectedIds([]);
              setActiveFilter(k == "active");
              setPageNumber(1);
              searchClientName(clientToSearch, k == "active");
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey="active" className="nav-link-client">
                {`Active ${
                  clientsCount?.active !== null && currentTab == "active"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="archived" className="nav-link-client">
                {`Archived ${
                  clientsCount?.archived !== null && currentTab == "archived"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <DataTable
            data={clients}
            columns={columns}
            selectable
            bordered
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            paginationEnabled
            pageSizeEnabled
          />
        </Card>
        <ConfirmationModal
          title="Confirm Delete"
          message={`Are you sure that you want to ${
            currentTab == "active" ? "archive" : "restore"
          } ${selectedIds.length} clients?`}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          show={showConfirmationModal}
          onHide={handleCancel}
        />
      </div>
    );
  }
};

export default List;
